import Modal from "@/components/shared/modal/modal";
import { usePartStateContext } from "@/pages/configurations/partState/context/PartsContext";
import { useVendorsContext } from "@/pages/configurations/vendors/context/VendorsContext";
import {
	Button,
	CircularProgress,
	FormControl,
	FormGroup,
	FormLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	Switch,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { usePartsContext } from "../../context/PartsContext";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Part } from "../../models/Part";
import { createPart } from "../../repositories/PartsRepository";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		checkbox: {
			userSelect: "none",
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const CreatePart = ({ show, onHide }: Props) => {
	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState("");
	const classes = useStyles();

	const {
		state: { parts },
	} = usePartsContext();
	const {
		state: { vendors },
	} = useVendorsContext();

	const {
		state: { partState },
	} = usePartStateContext();

	const {
		register,
		control,
		reset,
		handleSubmit,
		formState: { errors },
	} = useForm<Part>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const onSubmit: SubmitHandler<Part> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const res = await createPart(data);
			setIsSubmitLoading(false);

			onHide(true);
			//   setOpen(true)
			if (res.ok) {
				// onHide(true)
				setMessage("The Part was created successfully");
				setMessageType("success");
				setOpen(true);
			} else {
				setMessage("Error, please try again");
				setMessageType("error");
				setOpen(true);
			}
		} catch (e) {}
	};

	useEffect(() => {
		if (show) {
			reset();
		}
	}, [show]);

	return (
		<>
			<Modal
				open={show}
				handleClose={() => {
					onHide(true);
				}}
				id="createPart"
				title="Create a new part"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Modal.Content>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "300px" }}
							>
								{/* <LazyLoading /> */}
								<CircularProgress size="5rem" />
							</Grid>
						)}

						{!isLoading && (
							<>
								<Grid container spacing={4}>
									<Grid item md={12} xs={12}>
										<Controller
											name="partName"
											rules={{ required: true }}
											control={control}
											render={({ field }) => (
												<TextField
													label="Name"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.partName}
													fullWidth
													margin="dense"
												/>
											)}
										/>

										{errors.partName?.type === "required" && (
											<span className={classes.errorLabel}>
												Name is required
											</span>
										)}
									</Grid>
									<Grid item md={12} xs={12}>
										<Controller
											name="description"
											rules={{ required: true }}
											control={control}
											render={({ field }) => (
												<TextField
													label="Description"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.description}
													fullWidth
													margin="dense"
												/>
											)}
										/>

										{errors.description?.type === "required" && (
											<span className={classes.errorLabel}>
												Description is required
											</span>
										)}
									</Grid>
									<Grid item md={12} xs={12}>
										<Controller
											name="sku"
											rules={{ required: true }}
											control={control}
											render={({ field }) => (
												<TextField
													label="SKU"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.sku}
													fullWidth
													margin="dense"
												/>
											)}
										/>

										{errors.sku?.type === "required" && (
											<span className={classes.errorLabel}>
												SKU is required
											</span>
										)}
									</Grid>
									<Grid item md={12} xs={12}>
										<Controller
											name="unit"
											rules={{ required: true }}
											control={control}
											render={({ field }) => (
												<TextField
													label="Unit"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.unit}
													fullWidth
													margin="dense"
												/>
											)}
										/>

										{errors.unit?.type === "required" && (
											<span className={classes.errorLabel}>
												Unit is required
											</span>
										)}
									</Grid>
									<Grid item md={6} xs={12}>
										<Controller
											name="vendorId"
											control={control}
											rules={{
												required: true,
												validate: { positive: (v) => v != 0 },
											}}
											render={({ field }) => (
												<FormControl fullWidth>
													<InputLabel
														variant="standard"
														htmlFor="uncontrolled-native"
													>
														Vendor
													</InputLabel>
													<Select
														error={!!errors.vendorId}
														value={field.value}
														onChange={field.onChange}
													>
														{vendors.map((type) => {
															return (
																<MenuItem
																	value={type.vendorId}
																	key={type.vendorId}
																>
																	{type.vendorName}
																</MenuItem>
															);
														})}
													</Select>
												</FormControl>
											)}
										/>
										{errors.vendorId?.type === "positive" && (
											<span className={classes.errorLabel}>
												This field is required
											</span>
										)}
									</Grid>
									<Grid item md={6} xs={12}>
										<Controller
											name="partStateId"
											control={control}
											rules={{
												required: true,
												validate: { positive: (v) => v != 0 },
											}}
											render={({ field }) => (
												<FormControl fullWidth>
													<InputLabel
														variant="standard"
														htmlFor="uncontrolled-native"
													>
														Part State
													</InputLabel>
													<Select
														error={!!errors.partStateId}
														value={field.value}
														onChange={field.onChange}
													>
														{partState.map((type) => {
															return (
																<MenuItem
																	value={type.partStateId}
																	key={type.partStateId}
																>
																	{type.partStateName}
																</MenuItem>
															);
														})}
													</Select>
												</FormControl>
											)}
										/>
										{errors.partStateId?.type === "positive" && (
											<span className={classes.errorLabel}>
												This field is required
											</span>
										)}
									</Grid>
									<Grid item md={8} xs={12}>
										<Typography>Status</Typography>
										<Controller
											name="isActive"
											control={control}
											render={({ field }) => (
												<FormGroup>
													<Switch
														// className='switchBase'
														color="primary"
														defaultChecked={field.value}
														value={field.value}
														onChange={field.onChange}
													/>

													<FormLabel component="legend">
														{field.value ? "Active" : "Inactive"}
													</FormLabel>
												</FormGroup>
											)}
										/>
									</Grid>
								</Grid>
							</>
						)}
					</Modal.Content>
					<Modal.Actions style={{ padding: "1rem" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={() => {
										onHide(false);
									}}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</Modal.Actions>
				</form>
			</Modal>

			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				{messageType == "success" ? (
					<Alert severity="success" onClose={handleClose}>
						{message}
					</Alert>
				) : (
					<Alert severity="error" onClose={handleClose}>
						{message}
					</Alert>
				)}
			</Snackbar>
		</>
	);
};
